import { useTranslation } from 'react-i18next';

import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { cn } from '@/lib/utils';
import { StatusBadge, StatusBadgeType } from '@/modules/common';
import { getTimeOffStatusText } from '@/modules/common/components/molecules/badge/status-configs/time-off-status-config';
import { formatDisplayDate } from '@/utils/date-formatters';

import { TimeOffRequest } from '../../../types/time-off-request';
import { TimeOffStatus } from '../../../types/time-off-status';

interface TimeOffStatusCellProps {
  request: TimeOffRequest;
  isPast: boolean;
}

/**
 * Status badge with a hover tooltip (shared by the Time-Offs and Approvals tables):
 *   - APPROVED → "Time-off was approved on {approvedAt}"
 *   - PENDING  → "Time-off was requested on {requestedAt}"
 * Other statuses render the plain badge (no tooltip).
 */
export const TimeOffStatusCell = ({ request, isPast }: TimeOffStatusCellProps) => {
  const { t } = useTranslation('authenticated.timeOff');

  const tooltip = (() => {
    if (request.status === TimeOffStatus.Approved && request.approvedAt) {
      return t('approvals.statusTooltip.approved', { date: formatDisplayDate(request.approvedAt) });
    }
    if (request.status === TimeOffStatus.Pending) {
      return t('approvals.statusTooltip.requested', {
        date: formatDisplayDate(request.requestedAt),
      });
    }
    return null;
  })();

  const badge = (
    <span className={cn('inline-flex', isPast && 'opacity-50')}>
      <StatusBadge
        type={StatusBadgeType.TimeOff}
        status={request.status}
        label={getTimeOffStatusText(request.status)}
      />
    </span>
  );

  if (!tooltip) {
    return badge;
  }

  return <SimpleTooltip content={tooltip}>{badge}</SimpleTooltip>;
};
